import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/useToast';
import { useCreateNotificationMutation } from '@/lib/api-extensions/admin-queries';
import { useSuspenseQuery } from '@tanstack/react-query';
import { organizationsQueryOptions } from '@/lib/query-options';
import { Bell, Building2, Info, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

type WarningLevel = 'INFO' | 'WARNING' | 'CRITICAL' | 'SUCCESS';

interface NotificationFormData {
  title: string;
  subtitle: string;
  content: string;
  source: string;
  warning_level: WarningLevel;
  organization_id: number | null;
}

interface ManualNotificationCreatorProps {
  className?: string;
}

export function ManualNotificationCreator({ className }: ManualNotificationCreatorProps) {

  const [formData, setFormData] = useState<NotificationFormData>({
    title: '',
    subtitle: '',
    content: '',
    source: 'manual',
    warning_level: 'INFO',
    organization_id: null,
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  
  // Fetch organizations for selection
  const { data: organizations } = useSuspenseQuery(organizationsQueryOptions());
  
  const createNotificationMutation = useCreateNotificationMutation();

  const handleInputChange = (field: keyof NotificationFormData, value: string | number | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title and content are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createNotificationMutation.mutateAsync({
        title: formData.title,
        subtitle: formData.subtitle || undefined,
        content: formData.content,
        source: formData.source,
        warning_level: formData.warning_level,
        organization_id: formData.organization_id,
      });
      
      setIsSuccess(true);
      
      // Reset form
      setFormData({
        title: '',
        subtitle: '',
        content: '',
        source: 'manual',
        warning_level: 'INFO',
        organization_id: null,
      });
      
      toast({
        title: 'Success',
        description: 'Notification created successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create notification',
        variant: 'destructive',
      });
    }
  };

  const getWarningLevelIcon = (level: WarningLevel) => {
    switch (level) {
      case 'INFO':
        return <Info className="h-4 w-4" />;
      case 'WARNING':
        return <AlertCircle className="h-4 w-4" />;
      case 'CRITICAL':
        return <AlertCircle className="h-4 w-4" />;
      case 'SUCCESS':
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  const getWarningLevelColor = (level: WarningLevel) => {
    switch (level) {
      case 'INFO':
        return 'text-blue-600';
      case 'WARNING':
        return 'text-amber-600';
      case 'CRITICAL':
        return 'text-red-600';
      case 'SUCCESS':
        return 'text-green-600';
    }
  };
  
  return (
    <Card className={cn("w-full max-w-2xl", className)}>
      <CardHeader className="px-6 py-6">
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Create Manual Notification
        </CardTitle>
        <CardDescription className="mt-2">
          Create a custom notification for users. Select an organization to send to specific users,
          or leave empty to send to all users.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Organization Selection */}
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Organization (optional)
          </Label>
          <Select 
            value={formData.organization_id?.toString() || ''} 
            onValueChange={(value) => handleInputChange('organization_id', value ? Number(value) : null)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select an organization (or leave empty for all)" />
            </SelectTrigger>
            <SelectContent>
              {organizations?.map((org) => (
                <SelectItem key={org.id} value={org.id.toString()}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Leave empty to send notification to all users
          </p>
        </div>

        {/* Warning Level */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Warning Level</Label>
          <Select 
            value={formData.warning_level} 
            onValueChange={(value) => handleInputChange('warning_level', value as WarningLevel)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INFO">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-600" />
                  <span>Info</span>
                </div>
              </SelectItem>
              <SelectItem value="WARNING">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span>Warning</span>
                </div>
              </SelectItem>
              <SelectItem value="CRITICAL">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span>Critical</span>
                </div>
              </SelectItem>
              <SelectItem value="SUCCESS">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Success</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title" className="text-sm font-medium">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Notification title"
            className="w-full"
            maxLength={500}
          />
        </div>

        {/* Subtitle */}
        <div className="space-y-2">
          <Label htmlFor="subtitle" className="text-sm font-medium">Subtitle (optional)</Label>
          <Input
            id="subtitle"
            value={formData.subtitle}
            onChange={(e) => handleInputChange('subtitle', e.target.value)}
            placeholder="Optional subtitle"
            className="w-full"
            maxLength={1000}
          />
        </div>

        {/* Content */}
        <div className="space-y-2">
          <Label htmlFor="content" className="text-sm font-medium">Content *</Label>
          <Textarea
            id="content"
            value={formData.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            placeholder="Notification content/body"
            className="w-full min-h-[120px]"
          />
        </div>

        {/* Source */}
        <div className="space-y-2">
          <Label htmlFor="source" className="text-sm font-medium">Source</Label>
          <Input
            id="source"
            value={formData.source}
            onChange={(e) => handleInputChange('source', e.target.value)}
            placeholder="Notification source identifier"
            className="w-full"
            maxLength={100}
          />
          <p className="text-xs text-muted-foreground">
            Used to identify where this notification came from
          </p>
        </div>

        {/* Preview */}
        {(formData.title || formData.content) && (
          <div className="rounded-lg border bg-gray-50 p-4 space-y-3">
            <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
              {getWarningLevelIcon(formData.warning_level)}
              Preview
            </div>
            <div className="space-y-2">
              {formData.title && (
                <div className="font-medium text-gray-900">{formData.title}</div>
              )}
              {formData.subtitle && (
                <div className="text-sm text-gray-700">{formData.subtitle}</div>
              )}
              {formData.content && (
                <div className="text-sm text-gray-900 whitespace-pre-wrap">{formData.content}</div>
              )}
              <div className={`flex items-center gap-2 text-xs ${getWarningLevelColor(formData.warning_level)}`}>
                {getWarningLevelIcon(formData.warning_level)}
                <span>{formData.warning_level}</span>
                {formData.source && <span>• {formData.source}</span>}
              </div>
            </div>
          </div>
        )}
        
        {/* Create Button */}
        <Button 
          onClick={handleCreate}
          disabled={createNotificationMutation.isPending || !formData.title.trim() || !formData.content.trim()}
          className="w-full"
        >
          {createNotificationMutation.isPending ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Creating Notification...
            </>
          ) : (
            <>
              <Bell className="mr-2 h-4 w-4" />
              Create Notification
            </>
          )}
        </Button>
        
        {/* Success Message */}
        {isSuccess && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>Notification created successfully!</strong>
              <br />
              The notification has been created and will be visible to the selected users in their notifications inbox.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}